import React, { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCw, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const POSITION_LABELS = { front: "Front", "3quarter": "Angled", side: "Side/Temple", folded: "Inside Arm", extra: "Detail" };

export function ProductGallery({ images, alt }) {
  const [activeImg, setActiveImg] = useState(0);
  const [mode360, setMode360] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const drag = useRef({ dragging: false, startX: 0, startIndex: 0 });
  const can360 = images.length >= 3;

  const clampIndex = (i) => ((i % images.length) + images.length) % images.length;

  const startDrag = (clientX) => {
    if (!mode360) return;
    drag.current = { dragging: true, startX: clientX, startIndex: activeImg };
  };
  const moveDrag = (clientX) => {
    if (!mode360 || !drag.current.dragging) return;
    const delta = clientX - drag.current.startX;
    const framesMoved = Math.round(delta / 45);
    setActiveImg(clampIndex(drag.current.startIndex - framesMoved));
  };
  const endDrag = () => {
    drag.current.dragging = false;
  };

  if (!images.length) {
    return <div className="aspect-[16/10] bg-white rounded-sm mb-3" data-testid="product-gallery-empty" />;
  }

  return (
    <div>
      <div
        className={`relative aspect-square bg-[#F2F2F4] border border-neutral-200/70 rounded-2xl mb-4 overflow-hidden select-none ${mode360 ? "cursor-grab active:cursor-grabbing" : ""}`}
        data-testid="product-gallery"
        onMouseDown={(e) => startDrag(e.clientX)}
        onMouseMove={(e) => moveDrag(e.clientX)}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={(e) => startDrag(e.touches[0].clientX)}
        onTouchMove={(e) => moveDrag(e.touches[0].clientX)}
        onTouchEnd={endDrag}
      >
        {images[activeImg] && (
          <img
            src={images[activeImg].url}
            alt={alt}
            className="w-full h-full object-contain p-6 pointer-events-none"
            data-testid="product-main-image"
            draggable={false}
          />
        )}
        {images[activeImg]?.position && POSITION_LABELS[images[activeImg].position] && (
          <span className="absolute bottom-3 left-3 bg-white/90 text-[11px] font-heading font-bold uppercase tracking-wide text-neutral-600 px-3 py-1.5 rounded-full" data-testid="gallery-position-label">
            {POSITION_LABELS[images[activeImg].position]}
          </span>
        )}
        {!mode360 && (
          <button
            onClick={() => setZoomOpen(true)}
            className="absolute top-3 right-3 bg-white/90 text-neutral-700 w-10 h-10 rounded-full shadow-sm flex items-center justify-center hover:scale-105 transition-transform"
            aria-label="Zoom image"
            data-testid="gallery-zoom-button"
          >
            <ZoomIn size={16} />
          </button>
        )}
        {!mode360 && images.length > 1 && (
          <>
            <button
              onClick={() => setActiveImg(clampIndex(activeImg - 1))}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 text-zinc-700 w-10 h-10 rounded-full shadow-sm flex items-center justify-center hover:scale-105 transition-transform"
              aria-label="Previous image"
              data-testid="gallery-prev-button"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setActiveImg(clampIndex(activeImg + 1))}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 text-zinc-700 w-10 h-10 rounded-full shadow-sm flex items-center justify-center hover:scale-105 transition-transform"
              aria-label="Next image"
              data-testid="gallery-next-button"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
        {can360 && (
          <button
            onClick={() => setMode360((m) => !m)}
            className={`absolute bottom-3 right-3 px-4 py-2 rounded-full text-[11px] font-heading font-bold uppercase tracking-wide flex items-center gap-1.5 transition-all ${
              mode360 ? "bg-teal-600 text-white" : "bg-white/90 text-neutral-700 hover:bg-white"
            }`}
            data-testid="toggle-360-view-button"
          >
            <RotateCw size={13} /> {mode360 ? "Drag To Spin" : "360° View"}
          </button>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto">
        {images.map((img, i) => (
          <button
            key={img.id}
            onClick={() => {
              setActiveImg(i);
              setMode360(false);
            }}
            className={`w-16 h-16 shrink-0 rounded-xl overflow-hidden border-2 bg-[#F2F2F4] ${i === activeImg ? "border-teal-600" : "border-neutral-100"}`}
            data-testid={`thumbnail-${i}`}
          >
            <img src={img.url} alt="" className="w-full h-full object-cover p-1" />
          </button>
        ))}
      </div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-w-4xl bg-white p-0 overflow-hidden" data-testid="gallery-zoom-modal">
          <DialogTitle className="sr-only">{alt} — Zoomed Image</DialogTitle>
          {images[activeImg] && (
            <img src={images[activeImg].url} alt={alt} className="w-full h-full object-contain max-h-[85vh] p-6" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
